// src/utils/allocationShape.js
// v0.33 build 75 — CLIENT MIRROR of the server's allocation coherence matrix
// (api server.js validateAllocationShape). Separate repos can't share the module, so
// the matrix logic is duplicated here — KEEP IN SYNC with the server (as Build 64 did
// for account order). Three valid states for a MANUAL expense; anything else is a
// violation:
//   1 EXCLUDED     cost_class='none', scope NULL, entity NULL
//   2 UNCLASSIFIED cost_class NULL,   scope NULL, entity NULL
//   3 ALLOCATED    cost_class in {fixed,variable} + scope + a matching entity
// scope 'location' is the "site" scope; its entity is a location/site id.
export const ALLOC_SCOPES_WITH_ENTITY = ['band', 'farm', 'location'];

export function validateAllocationShape(costClass, scope, entityType, entityId) {
  const cc = costClass || null;
  const sc = scope || null;
  const hasEntity = !!entityId;
  const et = hasEntity ? (entityType || null) : null;
  if (cc === 'none') {
    if (sc === null && !hasEntity) return { ok: true };
    return { ok: false, field: 'scope', error: 'An excluded expense (Allocation: None) must carry no scope and no entity.' };
  }
  if (cc === null) {
    if (sc === null && !hasEntity) return { ok: true };
    return { ok: false, field: 'cost_class', error: 'This expense has a scope or entity but no cost class — allocate it (cost class + scope + a matching entity) or set Allocation to None.' };
  }
  if (cc === 'variable') {
    if (sc === null) return { ok: false, field: 'scope', error: 'A variable expense needs a scope (band, farm or site).' };
    if (sc === 'global') return { ok: false, field: 'scope', error: 'A variable expense cannot be global — choose band, farm or site.' };
    if (!ALLOC_SCOPES_WITH_ENTITY.includes(sc)) return { ok: false, field: 'scope', error: `Invalid scope "${sc}".` };
    if (!hasEntity) return { ok: false, field: 'entity', error: `Scope is ${sc} — select a ${sc}.` };
    if (et !== sc) return { ok: false, field: 'entity', error: `Scope is ${sc} but the selected entity is a ${et}.` };
    return { ok: true };
  }
  if (cc === 'fixed') {
    if (sc === null) return { ok: false, field: 'scope', error: 'A fixed expense needs a scope (band, farm, site or global).' };
    if (sc === 'global') {
      if (hasEntity) return { ok: false, field: 'entity', error: 'A global fixed expense must not carry a band/farm/site entity.' };
      return { ok: true };
    }
    if (!ALLOC_SCOPES_WITH_ENTITY.includes(sc)) return { ok: false, field: 'scope', error: `Invalid scope "${sc}".` };
    if (!hasEntity) return { ok: false, field: 'entity', error: `Scope is ${sc} — select a ${sc}.` };
    if (et !== sc) return { ok: false, field: 'entity', error: `Scope is ${sc} but the selected entity is a ${et}.` };
    return { ok: true };
  }
  return { ok: false, field: 'cost_class', error: `Invalid cost class "${cc}".` };
}

// Derive the allocation entity from a row-like object BY SCOPE and validate (mirrors
// the server's allocationErrorForRow). Returns an error string or null.
export function allocationErrorForRow(row) {
  const sc = row.scope || null;
  let entityType = null, entityId = null;
  if (sc === 'band')          { entityType = 'band';     entityId = row.band_id     || null; }
  else if (sc === 'farm')     { entityType = 'farm';     entityId = row.farm_id     || null; }
  else if (sc === 'location') { entityType = 'location'; entityId = row.location_id || null; }
  else {
    const stray = [['band', row.band_id], ['farm', row.farm_id], ['location', row.location_id]].filter(([, v]) => v);
    entityType = stray[0] ? stray[0][0] : null;
    entityId = stray[0] ? stray[0][1] : null;
  }
  const r = validateAllocationShape(row.cost_class || null, sc, entityType, entityId);
  return r.ok ? null : r.error;
}
